import { zodResolver } from '@hookform/resolvers/zod';
import { Minus, Plus } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { useContactPersons } from '@/api/domains/contact-persons';
import { useCreateCourierPickup, usePickupCutoff } from '@/api/domains/courier-pickups';
import { usePickupLocations } from '@/api/domains/pickup-locations';
import { FormComboboxField, FormDateField, FormSelectField } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { createPickupOrderSchema, type CreatePickupOrderFormValues } from '../settings/schemas';
import {
  DEFAULT_CUTOFF_HOUR,
  formatHour,
  fromHourOptions,
  pickupDayMatcher,
  toHourOptions,
} from './pickup-time';

const DEFAULTS: CreatePickupOrderFormValues = {
  pickup_location_id: '',
  contact_person_id: '',
  pickup_date: '',
  hour_from: '',
  hour_to: '',
  count: 1,
  weight: 1,
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CreatePickupOrderDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation('pickup-orders');

  const locations = usePickupLocations();
  const contacts = useContactPersons();
  const create = useCreateCourierPickup();

  const form = useForm<CreatePickupOrderFormValues>({
    resolver: zodResolver(createPickupOrderSchema),
    defaultValues: DEFAULTS,
  });
  const { control, setValue, handleSubmit, formState } = form;

  const locationId = useWatch({ control, name: 'pickup_location_id' });
  const pickupDate = useWatch({ control, name: 'pickup_date' });
  const hourFrom = useWatch({ control, name: 'hour_from' });
  const count = useWatch({ control, name: 'count' });
  const weight = useWatch({ control, name: 'weight' });

  const cutoffQuery = usePickupCutoff(locationId ? Number(locationId) : null);
  const cutoffHour = cutoffQuery.data?.hour ?? DEFAULT_CUTOFF_HOUR;

  // Cut-off (from /pickups/v1/cutoff-time for the location's postal code) bounds the
  // selectable days and hours.
  const dayMatcher = useMemo(() => pickupDayMatcher(cutoffHour), [cutoffHour]);
  const fromOptions = useMemo(
    () => (pickupDate ? fromHourOptions(pickupDate, cutoffHour) : []),
    [pickupDate, cutoffHour],
  );
  const toOptions = useMemo(
    () => (hourFrom ? toHourOptions(Number(hourFrom), cutoffHour) : []),
    [hourFrom, cutoffHour],
  );

  const hasAddressBook = (locations.data?.length ?? 0) > 0 && (contacts.data?.length ?? 0) > 0;

  // Seed defaults (default location/contact, else first) when the dialog opens.
  useEffect(() => {
    if (!open) return;
    const defaultLocation = locations.data?.find((l) => l.is_default) ?? locations.data?.[0];
    const defaultContact = contacts.data?.find((c) => c.is_default) ?? contacts.data?.[0];
    form.reset({
      ...DEFAULTS,
      pickup_location_id: defaultLocation ? String(defaultLocation.id) : '',
      contact_person_id: defaultContact ? String(defaultContact.id) : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locations.data, contacts.data]);

  // The cut-off (and thus the valid days/hours) depends on the location; clear the
  // window when it changes so a stale selection can't be submitted.
  useEffect(() => {
    setValue('pickup_date', '');
    setValue('hour_from', '');
    setValue('hour_to', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    setValue('hour_from', '');
    setValue('hour_to', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupDate]);

  useEffect(() => {
    setValue('hour_to', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourFrom]);

  const onSubmit = (values: CreatePickupOrderFormValues) => {
    create.mutate(
      {
        pickup_location_id: Number(values.pickup_location_id),
        contact_person_id: Number(values.contact_person_id),
        pickup_date: values.pickup_date,
        hour_from: Number(values.hour_from),
        hour_to: Number(values.hour_to),
        count: values.count,
        weight: values.weight,
      },
      {
        onSuccess: () => {
          toast.success(t('create.success'));
          onOpenChange(false);
        },
        onError: () => toast.error(t('create.error')),
      },
    );
  };

  const locationOptions =
    locations.data?.map((l) => ({
      value: String(l.id),
      label: l.name,
      description: `${l.street} ${l.house_number}, ${l.postal_code} ${l.city}`,
    })) ?? [];
  const contactOptions =
    contacts.data?.map((c) => ({
      value: String(c.id),
      label: c.full_name,
      description: c.email,
    })) ?? [];

  const adjustCount = (delta: number) =>
    setValue('count', Math.max(1, (count ?? 1) + delta), { shouldValidate: true });

  const stepperButton =
    'grid h-9 w-[42px] place-items-center border border-input bg-white hover:bg-accent';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[666px]">
        <DialogHeader>
          <DialogTitle>{t('create.title')}</DialogTitle>
          <DialogDescription>{t('create.description')}</DialogDescription>
        </DialogHeader>

        {!hasAddressBook ? (
          <div className="flex flex-col gap-3 py-2">
            <p className="text-sm text-muted-foreground">{t('create.emptyAddressBook')}</p>
            <div className="flex flex-wrap gap-2">
              {(locations.data?.length ?? 0) === 0 ? (
                <Button asChild variant="outline">
                  <Link to="/settings?tab=pickup-locations">{t('create.addLocation')}</Link>
                </Button>
              ) : null}
              {(contacts.data?.length ?? 0) === 0 ? (
                <Button asChild variant="outline">
                  <Link to="/settings?tab=contact-persons">{t('create.addContact')}</Link>
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-7">
              <FormComboboxField<CreatePickupOrderFormValues>
                name="pickup_location_id"
                label={t('create.pickupLocation')}
                options={locationOptions}
                placeholder={t('create.selectLocation')}
                searchPlaceholder={t('create.searchLocation')}
                emptyText={t('create.noResults')}
                required
                modal
              />

              <FormComboboxField<CreatePickupOrderFormValues>
                name="contact_person_id"
                label={t('create.contactPerson')}
                options={contactOptions}
                placeholder={t('create.selectContact')}
                searchPlaceholder={t('create.searchContact')}
                emptyText={t('create.noResults')}
                required
                modal
              />

              <div className="flex flex-col gap-2.5">
                <span className="text-sm font-medium text-black">{t('create.pickupTime')}</span>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_140px_140px]">
                  <FormDateField<CreatePickupOrderFormValues>
                    name="pickup_date"
                    placeholder={t('create.chooseDay')}
                    disabledDates={dayMatcher}
                    modal
                  />
                  <FormSelectField<CreatePickupOrderFormValues>
                    name="hour_from"
                    options={fromOptions.map((h) => ({ value: String(h), label: formatHour(h) }))}
                    placeholder={t('create.from')}
                    disabled={!pickupDate}
                  />
                  <FormSelectField<CreatePickupOrderFormValues>
                    name="hour_to"
                    options={toOptions.map((h) => ({ value: String(h), label: formatHour(h) }))}
                    placeholder={t('create.until')}
                    disabled={!hourFrom}
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-start gap-12">
                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium text-black">
                    {t('create.shipmentCount')}
                  </span>
                  <div className="flex items-center gap-px">
                    <button
                      type="button"
                      onClick={() => adjustCount(-1)}
                      className={`${stepperButton} rounded-l-[10px]`}
                      aria-label={t('create.decrease')}
                    >
                      <Minus className="size-3.5" />
                    </button>
                    <Input
                      type="number"
                      min={1}
                      value={count ?? 1}
                      onChange={(e) =>
                        setValue('count', Math.max(1, Number(e.target.value) || 1), {
                          shouldValidate: true,
                        })
                      }
                      className="h-9 w-[66px] rounded-none border-input text-center shadow-none focus-visible:ring-0"
                      aria-label={t('create.shipmentCount')}
                    />
                    <button
                      type="button"
                      onClick={() => adjustCount(1)}
                      className={`${stepperButton} rounded-r-[10px]`}
                      aria-label={t('create.increase')}
                    >
                      <Plus className="size-3.5" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium text-black">{t('create.totalWeight')}</span>
                  <div className="flex items-center gap-[7px]">
                    <Input
                      type="number"
                      inputMode="decimal"
                      min={0.1}
                      step={0.1}
                      value={weight ?? ''}
                      onChange={(e) =>
                        setValue('weight', e.target.value === '' ? 0 : Number(e.target.value), {
                          shouldValidate: true,
                        })
                      }
                      className="h-9 w-[114px] rounded-[10px] border-input shadow-none"
                      aria-label={t('create.totalWeight')}
                    />
                    <div className="grid h-9 w-[42px] place-items-center rounded-[10px] border border-input bg-[#f5f5f5] text-sm text-foreground">
                      kg
                    </div>
                  </div>
                  {formState.errors.weight ? (
                    <p className="text-sm text-destructive">{formState.errors.weight.message}</p>
                  ) : null}
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={create.isPending}
                >
                  {t('create.close')}
                </Button>
                <Button type="submit" disabled={create.isPending}>
                  {create.isPending ? t('create.submitting') : t('create.submit')}
                </Button>
              </DialogFooter>
            </form>
          </FormProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}
